/**
 * Core-100 CORE-BO (GAP-003) — the "Open capacity" surface.
 *
 * Shows today's openings (cause, technician, minutes) and, for one opening, the ranked pull-forward
 * candidates with a "Why not?" list of blocked tickets. A person decides: "Pull forward" moves the ticket
 * through reassign-ticket with the governed reason `capacity_recovery_pull_forward` and records the
 * opening as filled; "Leave unfilled" records the reason. Leaving capacity unfilled is always permitted.
 *
 * oct20e (FD-2026-10-24-A): in the shop's automatic mode (the Core default) the server's automatic recovery fills
 * eligible openings itself (`_shared/capacityRecoveryAuto.ts`); this panel then also says what it moved and why.
 * Manual mode: every opening waits here for a person, as before.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';
import { clock } from '@/utils/clock';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from '@/hooks/use-toast';
import { edgeRefusalMessage } from '@/utils/assignment/smartAssignRefusal';
import { capacityRecoveryPolicy, describeAutoFill, describeBlockers, describeClaimRefusal, fillOpening, fillTargetTechId, OPEN_CAPACITY_TICKET_COLUMNS, OPENING_CAUSE_LABEL, planOpening, type AutoFilledOpeningRow, type CapacityOpeningRow, type FillClient } from '@/lib/capacity/openCapacity';

export function OpenCapacityPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const shopId = activeShop?.id;
  const today = getTodayInShopTz(shopTimezone);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [busy, setBusy] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['open-capacity', shopId, today],
    enabled: open && !!shopId,
    queryFn: async () => {
      const [openings, tickets, culture, techs, autoFilled] = await Promise.all([
        supabase.from('capacity_openings_current' as never).select('*').eq('shop_id', shopId!).eq('effective_status', 'open').eq('shop_day', today).order('window_start'),
        supabase.from('tickets').select(OPEN_CAPACITY_TICKET_COLUMNS).eq('shop_id', shopId!).is('deleted_at', null).gte('scheduled_date', today).limit(500),
        supabase.from('shop_profile').select('shop_culture').eq('shop_id', shopId!).maybeSingle(),
        supabase.from('technicians').select('id, name').eq('shop_id', shopId!),
        // oct20e (Bucket 5 :1102 "in automatic mode, Core must inform the user of what changed and why"): today's automatic fills
        supabase.from('capacity_openings' as never).select('id, cause, technician_id, filled_ticket_id, resolved_at, recovered_minutes')
          .eq('shop_id', shopId!).eq('shop_day', today).eq('status', 'filled').eq('resolution_mode', 'automatic').order('resolved_at', { ascending: false }).limit(20),
      ]);
      for (const r of [openings, tickets, culture, techs, autoFilled]) if (r.error) throw r.error;
      const policy = capacityRecoveryPolicy(culture.data?.shop_culture);
      return {
        openings: (openings.data ?? []) as unknown as CapacityOpeningRow[],
        tickets: (tickets.data ?? []) as never[],
        // oct20e (FD-2026-10-24-A; CORE-BO :74 as amended): permitted unless the shop restricts it
        allowFutureDay: policy.allowFutureDay,
        automatic: policy.automatic,
        autoFilled: (autoFilled.data ?? []) as unknown as AutoFilledOpeningRow[],
        techNames: Object.fromEntries((techs.data ?? []).map((t) => [t.id, t.name])) as Record<string, string>,
      };
    },
  });

  const opening = data?.openings.find((o) => o.id === selected) ?? data?.openings[0] ?? null;
  const plans = useMemo(() => (opening && data ? planOpening(opening, data.tickets, { now: clock.now(), allowFutureDay: data.allowFutureDay }) : []), [opening, data]);
  const eligible = plans.filter((p) => p.eligible);
  const blocked = plans.filter((p) => !p.eligible && !p.blockers.includes('terminal_state') && !p.blockers.includes('not_a_pull_forward_candidate'));
  const ticketName = (id: string) => (data?.tickets as Array<{ id: string; customer_name?: string | null }> | undefined)?.find((t) => t.id === id)?.customer_name || id;

  const refresh = () => qc.invalidateQueries({ queryKey: ['open-capacity', shopId, today] });

  const pullForward = async (ticketId: string) => {
    // A bay opening may name no technician: the candidate keeps its own (its chain owner — a parked car has only its plan).
    const candidate = (data?.tickets as Array<{ id: string; assigned_tech_id?: string | null; scheduled_tech_id?: string | null; parking_lot?: boolean | null }> | undefined)?.find((t) => t.id === ticketId);
    const targetTechId = fillTargetTechId(opening, candidate);
    if (!opening || !targetTechId) return;
    setBusy(true);
    // Core-100 R4: claim → move → resolve (or release when the move is refused). One filler per opening.
    const outcome = await fillOpening(supabase as unknown as FillClient, {
      openingId: opening.id, ticketId, targetTechId, scheduledDate: today, windowStart: opening.window_start,
    });
    setBusy(false);
    if ('stage' in outcome && outcome.stage === 'claim') {
      toast({ title: 'Not pulled forward', description: describeClaimRefusal(outcome.message), variant: 'destructive' });
    } else if ('stage' in outcome) {
      toast({ title: 'Could not pull forward', description: await edgeRefusalMessage(outcome.error as never, 'The move was refused; nothing changed.'), variant: 'destructive' });
    } else {
      toast({ title: 'Pulled forward', description: outcome.resolved ? `${ticketName(ticketId)} now starts in the opening.` : 'The ticket moved, but the opening could not be marked filled.' });
    }
    void refresh();
  };

  const dismiss = async () => {
    if (!opening || !dismissReason.trim()) return;
    setBusy(true);
    const { error } = await supabase.rpc('resolve_capacity_opening' as never, { p_opening_id: opening.id, p_outcome: 'dismissed', p_filled_ticket_id: null, p_reason: dismissReason.trim() } as never);
    setBusy(false);
    if (error) { toast({ title: 'Could not record that', description: 'Nothing changed.', variant: 'destructive' }); return; }
    setDismissReason('');
    void refresh();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="open-capacity-panel">
        <SheetHeader>
          <SheetTitle>Open capacity</SheetTitle>
          <SheetDescription>
            {data?.automatic === false
              ? 'Time that opened earlier than planned today, and what could safely move into it. Your shop recovers capacity by suggestion: nothing moves unless you choose it.'
              : 'Time that opened earlier than planned today, and what could safely move into it. Your shop recovers capacity automatically: OptiBay moves eligible work whose vehicle is here, on the same technician\'s schedule, and lists it below. You can still fill an opening yourself.'}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          {data && data.autoFilled.length > 0 && (
            <section className="space-y-1" data-testid="open-capacity-auto-filled">
              <p className="text-sm font-medium">Recovered automatically today ({data.autoFilled.length})</p>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {data.autoFilled.map((r) => (
                  <li key={r.id}>{describeAutoFill(r, r.filled_ticket_id ? ticketName(r.filled_ticket_id) : 'A ticket', (r.technician_id && data.techNames[r.technician_id]) || null, shopTimezone)}</li>
                ))}
              </ul>
            </section>
          )}
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {isError && <p role="alert" className="text-sm text-destructive">Could not load open capacity.</p>}
          {data && data.openings.length === 0 && <p className="text-sm text-muted-foreground">No open capacity right now.</p>}
          {data && data.openings.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.openings.map((o) => (
                <Button key={o.id} size="sm" variant={opening?.id === o.id ? 'default' : 'outline'} onClick={() => setSelected(o.id)}>
                  {(o.technician_id && data.techNames[o.technician_id]) || 'Technician'} — {OPENING_CAUSE_LABEL[o.cause] ?? o.cause}
                  {o.available_minutes != null ? ` · ${o.available_minutes} min` : ''}
                  {o.being_filled ? ' · being filled' : ''}
                </Button>
              ))}
            </div>
          )}
          {opening && (
            <>
              <section className="space-y-2" data-testid="open-capacity-candidates">
                <p className="text-sm font-medium">Could move in ({eligible.length})</p>
                {eligible.length === 0 && <p className="text-xs text-muted-foreground">Nothing can safely move into this opening.</p>}
                {eligible.map((p) => (
                  <div key={p.ticketId} className="flex items-center justify-between gap-2 rounded-md border p-2">
                    <div className="min-w-0">
                      <p className="text-sm truncate">{ticketName(p.ticketId)}</p>
                      <Badge variant="secondary" className="mt-1">{p.kind.replace(/_/g, ' ')}</Badge>
                    </div>
                    <Button size="sm" disabled={busy} onClick={() => void pullForward(p.ticketId)}>Pull forward</Button>
                  </div>
                ))}
              </section>
              {blocked.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground">Why not the others? ({blocked.length})</summary>
                  <ul className="mt-2 space-y-1">
                    {blocked.map((p) => <li key={p.ticketId}><span className="font-medium">{ticketName(p.ticketId)}</span>: {describeBlockers(p)}</li>)}
                  </ul>
                </details>
              )}
              <section className="space-y-2 border-t pt-3">
                <p className="text-sm font-medium">Leave it unfilled</p>
                <div className="flex gap-2">
                  <Input value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} placeholder="Why (e.g. tech on cleanup)" />
                  <Button variant="outline" disabled={busy || !dismissReason.trim()} onClick={() => void dismiss()}>Record</Button>
                </div>
              </section>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
